import {useState} from 'react'
import {useParams, useNavigate, Link} from 'react-router-dom'
import {useDialog} from '@/hooks/useDialog'
import {parseCronExpression} from '@/lib/cron-utils'
import {Play, Trash2, Lock, Pencil, Code, ChevronDown, ChevronUp, Loader2} from 'lucide-react'
import {routes} from '@/lib/routes'
import {notFoundIfMissing} from '@/lib/not-found-error'
import {usePageTitle} from '@/hooks/use-page-title'
import {useJobExecutionsStream} from '@/hooks/use-job-executions-stream'
import {useJob} from '@/features/jobs/hooks/useJob'
import {useJobMutations} from '@/features/jobs/hooks/useJobMutations'
import {Button} from '@/components/ui/button'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {Switch} from '@/components/ui/switch'
import {Input} from '@/components/ui/input'
import {Progress} from '@/components/ui/progress'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {CONCEPT_ICON} from '@/lib/concepts'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {ErrorBanner} from '@/components/ui/error-banner'
import {useAuth} from '@/contexts/auth-context'
import {useI18n} from '@/i18n'
import {CodeBlock} from '@/components/shared/CodeBlock'
import {Strong} from '@/components/shared/Strong'
import {Stack} from '@/components/shared/Stack'
import {permissions} from '@/lib/permissions'
import {HandlerType, JobExecutionStatus, JobSource} from '@/types/background-jobs'
import type {TaskDefinition, LogLevel} from '@/types/background-jobs'
import {StatusBadge, StatusBadgeType} from '@/components/shared/StatusBadge'
import {CodeText} from '@/components/shared/CodeText'
import {CardSection} from '@/components/shared/CardSection'
import {MutedText} from '@/components/shared/MutedText'
import {StatusText} from '@/components/shared/StatusText'
import {TextLink} from '@/components/shared/TextLink'
import {InlineRow} from '@/components/shared/InlineRow'
import {DetailField} from '@/components/shared/DetailField'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {PageLayout} from '@/components/shared/PageLayout'
import {Icon} from '@/components/shared/Icon'
import {PulseDot} from '@/components/shared/PulseDot'
import {formatDuration, formatTimestamp} from '@/lib/date-utils'

// Tasks are rendered as a flat sorted list (no tree structure)

interface TaskRowProps {
    task: TaskDefinition
    canAdmin: boolean
    jobId: string
    onUpdateLogLevel: (taskId: string, logLevel: LogLevel) => void
    isUpdating: boolean
    i18n: ReturnType<typeof useI18n>
}

function TaskRow({task, canAdmin, jobId, onUpdateLogLevel, isUpdating, i18n}: TaskRowProps) {
    const isStatic = task.source === JobSource.STATIC
    const isGroovyScript = task.handlerType === HandlerType.GROOVY_SCRIPT
    const [showScript, setShowScript] = useState(false)
    const scriptContent = isGroovyScript ? ((task.handlerConfig?.script as string) || '') : ''
    const scriptLines = scriptContent.split('\n')
    const isTruncated = scriptLines.length > 50
    const [showAllScript, setShowAllScript] = useState(false)
    const displayedScript = showAllScript ? scriptContent : scriptLines.slice(0, 50).join('\n')

    return (
        <Stack gap="xs">
            <InlineRow
                surface="hoverable"
                justify="between"
                className={isStatic ? 'text-muted-foreground' : undefined}
            >
                <InlineRow gap="md" grow minWidth="zero">
                    <MutedText size="xs" className="w-6 shrink-0">{task.sortOrder}</MutedText>
                    {isStatic ? <Icon icon={Lock} size="xs" className="shrink-0"/> : canAdmin ? <Icon icon={Pencil} size="xs" className="shrink-0"/> : null}
                    <Strong truncate>{task.name}</Strong>
                    <Badge variant="outline" className="text-xs shrink-0">{task.handlerType}</Badge>
                    {task.category && <Badge variant="secondary" className="text-xs shrink-0">{task.category}</Badge>}
                    {task.skipOnFailure && (
                        <Badge variant="warning" className="text-xs shrink-0">
                            {i18n.jobs.skipOnFail}
                        </Badge>
                    )}
                    {/* Script toggle for GROOVY_SCRIPT tasks */}
                    {isGroovyScript && scriptContent && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => setShowScript(!showScript)}
                            data-testid={`btn-toggle-script-${task.id}`}
                        >
                            {showScript ? <Icon icon={ChevronUp} size="xs"/> : <Icon icon={ChevronDown} size="xs"/>}
                        </Button>
                    )}
                </InlineRow>

                <InlineRow gap="lg" className="shrink-0">
                    {/* Edit in Builder link for GROOVY_SCRIPT + jobs:manage */}
                    {isGroovyScript && canAdmin && !isStatic && (
                        <TextLink
                            to={routes.backgroundJobs.builder.edit(jobId)}
                            testId={`link-edit-in-builder-${task.id}`}
                            stopPropagation
                        >
                            <InlineRow gap="xs">
                                <Icon icon={Code} size="xs"/>
                                {i18n.scriptEditor.editInBuilder}
                            </InlineRow>
                        </TextLink>
                    )}
                    {task.retryCount > 0 && (
                        <MutedText size="xs">
                            {task.retryCount === 1 ? i18n.jobs.retryCount({count: String(task.retryCount)}) : i18n.jobs.retryCountPlural({count: String(task.retryCount)})}
                        </MutedText>
                    )}
                    {task.timeoutMs && (
                        <MutedText size="xs">
                            {i18n.jobs.timeoutDuration({duration: formatDuration(task.timeoutMs)})}
                        </MutedText>
                    )}
                    <InlineRow gap="xs">
                        <MutedText size="xs">{i18n.jobs.log}</MutedText>
                        {canAdmin ? (
                            <Select
                                value={task.logLevel}
                                onValueChange={(value) => onUpdateLogLevel(task.id, value as LogLevel)}
                                disabled={isUpdating}
                            >
                                <SelectTrigger size="sm" className="w-20" data-testid="select-log-level">
                                    <SelectValue/>
                                </SelectTrigger>
                                {/* eslint-disable i18next/no-literal-string */}
                                <SelectContent>
                                <SelectItem value="DEBUG">DEBUG</SelectItem>
                                <SelectItem value="INFO">INFO</SelectItem>
                                <SelectItem value="WARN">WARN</SelectItem>
                                <SelectItem value="ERROR">ERROR</SelectItem>
                            </SelectContent>
                            {/* eslint-enable i18next/no-literal-string */}
                        </Select>
                    ) : (
                        <Badge variant="secondary" className="text-xs">{task.logLevel}</Badge>
                    )}
                </InlineRow>
                </InlineRow>
            </InlineRow>

            {/* Collapsible script preview for GROOVY_SCRIPT tasks */}
            {isGroovyScript && scriptContent && showScript && (
                <Stack gap="xs" className="ml-8" data-testid={`script-preview-${task.id}`}>
                    <CodeBlock>
                        {displayedScript}
                    </CodeBlock>
                    {isTruncated && (
                        <Button
                            type="button"
                            variant="link"
                            size="sm"
                            className="h-auto p-0"
                            onClick={() => setShowAllScript(!showAllScript)}
                            data-testid={`btn-toggle-full-script-${task.id}`}
                        >
                            {showAllScript ? i18n.scriptEditor.scriptPreview.collapse : i18n.scriptEditor.scriptPreview.showAll}
                        </Button>
                    )}
                </Stack>
            )}
        </Stack>
    )
}

export function JobDetail() {
    const {id} = useParams<{ id: string }>()
    const navigate = useNavigate()
    const {hasPermission} = useAuth()
    const i18n = useI18n()
    usePageTitle(i18n.jobs.detailPageTitle)
    const canAdmin = hasPermission(permissions.jobs.manage)

    // Subscribe to SSE for real-time execution updates
    const {isConnected: sseConnected} = useJobExecutionsStream({
        jobId: id || '',
        enabled: !!id,
    })

    // WHY: schedule edit state is declared before useJobMutations so the
    // mutation's onSuccess callback can close the editor without a forward ref.
    const [scheduleEditing, setScheduleEditing] = useState(false)
    const [cronInput, setCronInput] = useState('')
    const removeScheduleDialog = useDialog()
    const [scheduleError, setScheduleError] = useState<Error | null>(null)

    // Reads
    const {job, tasks, executions, isJobLoading} = useJob(id)
    const isDynamic = job?.source === JobSource.DYNAMIC

    // Mutations
    const {
        toggle: toggleMutation,
        trigger: triggerMutation,
        updateTaskLogLevel: updateLogLevelMutation,
        deleteJob: deleteMutation,
        updateSchedule: scheduleMutation,
    } = useJobMutations(id, {
        onScheduleSaved: () => {
            setScheduleEditing(false)
            setScheduleError(null)
        },
        onScheduleError: (error) => setScheduleError(error),
    })

    const cronFields = cronInput.trim().split(/\s+/)
    const cronValid = cronInput.trim() === '' || cronFields.length === 6
    const cronParsed = cronFields.length === 6 ? parseCronExpression(cronInput, {use24HourTimeFormat: true}) : {description: null, error: null}
    const cronPreview = cronParsed.description

    function handleScheduleSave() {
        if (!cronInput.trim()) {
            // Removing schedule — show confirmation
            removeScheduleDialog.open()
            return
        }
        scheduleMutation.mutate(cronInput.trim())
    }

    function handleRemoveScheduleConfirm() {
        removeScheduleDialog.close()
        scheduleMutation.mutate(null)
    }

    function handleScheduleCancel() {
        setScheduleEditing(false)
        setCronInput(job?.cronExpression || '')
        setScheduleError(null)
    }

    function handleScheduleEdit() {
        setCronInput(job?.cronExpression || '')
        setScheduleEditing(true)
        setScheduleError(null)
    }

    return (
        <PageLayout
            variant="detail"
            backTo={routes.backgroundJobs.jobs.list()}
            loading={isJobLoading}
            error={!isJobLoading ? notFoundIfMissing(job, 'JOB') : undefined}
            title={job?.name ?? ''}
            icon={CONCEPT_ICON.jobConfiguration}
            data-testid="job-detail-title"
            actions={job ? (
                <>
                    <StatusBadge status={job.source || ''} type={StatusBadgeType.SOURCE}/>
                    {job.enabled ? (
                        <Badge variant={BadgeVariant.SUCCESS}>{i18n.common.enabled}</Badge>
                    ) : (
                        <Badge variant={BadgeVariant.WARNING}>{i18n.common.disabled}</Badge>
                    )}
                    {canAdmin && (
                        <>
                            <Button
                                data-testid="btn-edit-tasks"
                                variant="outline"
                                onClick={() => navigate(routes.backgroundJobs.builder.edit(id!))}
                            >
                                <Icon icon={CONCEPT_ICON.jobConfiguration} className="mr-2"/>
                                {i18n.jobBuilder.editTasks}
                            </Button>
                            <Button
                                data-testid="btn-run-now"
                                onClick={() => triggerMutation.mutate()}
                                disabled={triggerMutation.isPending}
                            >
                                <Icon icon={Play} className="mr-2"/>
                                {i18n.jobs.runNow}
                            </Button>
                            {isDynamic && (
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <Button
                                            data-testid="btn-delete-job"
                                            variant="destructive"
                                            disabled={deleteMutation.isPending}
                                        >
                                            <Icon icon={Trash2} className="mr-2"/>
                                            {i18n.jobs.deleteJob}
                                        </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>{i18n.jobs.deleteConfirm({name: job.name})}</AlertDialogTitle>
                                            <AlertDialogDescription>{i18n.jobs.deleteConfirmMessage}</AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel>{i18n.common.cancel}</AlertDialogCancel>
                                            <AlertDialogAction
                                                variant="destructive"
                                                onClick={() => deleteMutation.mutate()}
                                            >
                                                {i18n.jobs.deleteJob}
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            )}
                        </>
                    )}
                </>
            ) : undefined}
        >
            {job && (
                <>
            {/* Configuration */}
            <CardSection data-testid="section-configuration" title={String(i18n.common.configuration)}>
                <DetailGrid columns={4} gap="sm">
                    <DetailField
                        label={i18n.jobs.schedule as string}
                        value={scheduleEditing ? (
                            <Stack gap="sm" data-testid="schedule-edit-form">
                                <Input
                                    value={cronInput}
                                    onChange={e => setCronInput(e.target.value)}
                                    placeholder={i18n.scriptEditor.schedule.cronFieldHint as string}
                                    size="sm"
                                    className="font-mono"
                                    data-testid="input-cron"
                                />
                                {cronInput.trim() && (
                                    <StatusText tone={cronPreview ? 'success' : 'destructive'} size="xs">
                                        {cronPreview || i18n.scriptEditor.schedule.invalidCron}
                                    </StatusText>
                                )}
                                <MutedText size="xs">{i18n.scriptEditor.schedule.hint}</MutedText>
                                {scheduleError && <ErrorBanner error={scheduleError}/>}
                                <InlineRow>
                                    <Button
                                        variant="default"
                                        size="sm"
                                        onClick={handleScheduleSave}
                                        disabled={(!cronValid || (cronInput.trim() !== '' && !cronPreview)) || scheduleMutation.isPending}
                                        data-testid="btn-schedule-save"
                                    >
                                        {scheduleMutation.isPending && <Icon icon={Loader2} size="xs" className="mr-1 animate-spin"/>}
                                        {i18n.scriptEditor.save}
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={handleScheduleCancel} data-testid="btn-schedule-cancel">
                                        {i18n.common.cancel}
                                    </Button>
                                </InlineRow>
                            </Stack>
                        ) : (
                            <InlineRow>
                                <CodeText>{job.cronExpression || i18n.jobs.manual}</CodeText>
                                {canAdmin && (
                                    <Button variant="ghost" size="icon-sm" onClick={handleScheduleEdit} data-testid="btn-edit-schedule">
                                        <Icon icon={Pencil} size="xs"/>
                                    </Button>
                                )}
                            </InlineRow>
                        )}
                    />
                    <DetailField label={i18n.jobs.timeout as string} value={formatDuration(job.timeoutMs)}/>
                    <DetailField label={i18n.common.enabled as string} value={
                        canAdmin ? (
                            <Switch
                                checked={job.enabled}
                                onCheckedChange={(enabled) => toggleMutation.mutate(enabled)}
                                disabled={toggleMutation.isPending}
                                data-testid="switch-enabled"
                            />
                        ) : (
                            job.enabled ? <Badge variant={BadgeVariant.SUCCESS}>{i18n.common.enabled}</Badge> :
                                <Badge variant={BadgeVariant.WARNING}>{i18n.common.disabled}</Badge>
                        )
                    }/>
                    <DetailField label={i18n.jobs.source as string} value={<StatusBadge status={job.source || ''} type={StatusBadgeType.SOURCE}/>}/>
                    <DetailField label={i18n.jobs.retries as string} value={job.retryCount}/>
                    <DetailField label={i18n.jobs.maxConcurrent as string} value={job.maxConcurrent}/>
                    <DetailField label={i18n.jobs.allowOverlap as string} value={job.allowOverlap ? i18n.common.yes : i18n.common.no}/>
                    <DetailField label={i18n.jobs.nextRun as string} value={formatTimestamp(job.nextRunTime)}/>
                </DetailGrid>
            </CardSection>

            {/* Task Chain */}
            <CardSection
                data-testid="section-tasks"
                title={`${String(i18n.jobs.tasks)} (${tasks.length})`}
                description={String(i18n.jobs.tasksDesc)}
            >
                {tasks.length === 0 ? (
                    <MutedText size="sm">{i18n.jobs.noTasksDefined}</MutedText>
                ) : (
                    <Stack gap="xs">
                        {[...tasks].sort((a, b) => a.sortOrder - b.sortOrder).map((task) => (
                            <TaskRow
                                key={task.id}
                                task={task}
                                canAdmin={canAdmin}
                                jobId={id!}
                                onUpdateLogLevel={(taskId, logLevel) => updateLogLevelMutation.mutate({
                                    taskId,
                                    logLevel
                                })}
                                isUpdating={updateLogLevelMutation.isPending}
                                i18n={i18n}
                            />
                        ))}
                    </Stack>
                )}
            </CardSection>

            {/* Recent Executions */}
            <CardSection
                data-testid="section-recent-executions"
                title={String(i18n.jobs.recentExecutions)}
                description={String(i18n.jobs.recentExecutionsDesc)}
            >
                <InlineRow justify="end" gap="lg" className="mb-4">
                    {sseConnected && (
                        <InlineRow gap="sm">
                            <PulseDot tone="success"/>
                            <MutedText size="xs">{i18n.jobs.live}</MutedText>
                        </InlineRow>
                    )}
                    <TextLink
                        // eslint-disable-next-line i18next/no-literal-string -- URL query param
                        to={routes.backgroundJobs.executions.list(`jobId=${id}`)}
                        testId="link-view-all-executions"
                    >
                        {i18n.backgroundJobs.viewAll}
                    </TextLink>
                </InlineRow>
                {executions.length === 0 ? (
                    <MutedText size="sm" block className="py-4">{i18n.jobs.noExecutionsYet}</MutedText>
                ) : (
                    <Stack gap="sm">
                        {executions.map((exec) => (
                            <Link
                                key={exec.id}
                                to={routes.backgroundJobs.executions.detail(exec.id)}
                                data-testid={`exec-row-${exec.id}`}
                            >
                                <InlineRow surface="hoverableLink" justify="between">
                                    <InlineRow gap="lg">
                                        <StatusBadge status={exec.status} type={StatusBadgeType.EXECUTION_STATUS}/>
                                        {(exec.status === JobExecutionStatus.RUNNING || exec.status === JobExecutionStatus.PENDING) && (
                                            <InlineRow gap="xs" minWidth="xs">
                                                <Progress value={exec.progress ?? 0} className="h-1.5 flex-1"/>
                                                <MutedText size="xs" className="w-8">
                                                    {exec.progress ?? 0}%
                                                </MutedText>
                                            </InlineRow>
                                        )}
                                    </InlineRow>
                                    <InlineRow gap="lg">
                                        <MutedText>{formatTimestamp(exec.startedAt)}</MutedText>
                                        <MutedText align="right" className="w-20">{formatDuration(exec.durationMs)}</MutedText>
                                    </InlineRow>
                                </InlineRow>
                            </Link>
                        ))}
                    </Stack>
                )}
            </CardSection>

                    {/* Remove Schedule Confirmation Dialog */}
                    <AlertDialog open={removeScheduleDialog.isOpen} onOpenChange={removeScheduleDialog.bind.onOpenChange}>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>{i18n.scriptEditor.schedule.removeTitle}</AlertDialogTitle>
                                <AlertDialogDescription>{i18n.scriptEditor.schedule.removeDescription}</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel data-testid="btn-cancel-remove-schedule">{i18n.common.cancel}</AlertDialogCancel>
                                <AlertDialogAction
                                    variant="destructive"
                                    onClick={handleRemoveScheduleConfirm}
                                    data-testid="btn-confirm-remove-schedule"
                                >
                                    {i18n.scriptEditor.schedule.removeConfirm}
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </>
            )}
        </PageLayout>
    )
}
